import { Button } from '@heroui/react';
import { cn } from '@hs/ui';
import {
  IconPlayerPause,
  IconPlayerPlay,
  IconRefresh,
  IconSettings,
} from '@tabler/icons-react';
import type { FC } from 'react';
import { useMemo } from 'react';

import { TIMER_INNER_DIAMETER_PERCENT } from './timerLayout';
import { TimerModeToggle } from './TimerModeToggle';
import { TimerProgressRing } from './TimerProgressRing';
import { TimerTimeSetting } from './TimerTimeSetting';
import {
  formatTimerTime,
  getDisplayFormat,
  getTimerMainDisplayMs,
  durationToMs,
} from './timeUtils';
import { getTimerControlView } from './timerControlState';
import { DEFAULT_TIMER_DURATION, DEFAULT_TIMER_MODE, type TimerProps } from './types';
import { useTimer } from './useTimer';

const ICON_STROKE = 1.25;
const DEFAULT_SIZE = 220;

export const Timer: FC<TimerProps> = ({
  size = DEFAULT_SIZE,
  className,
  style,
  defaultTarget = DEFAULT_TIMER_DURATION,
  defaultMode = DEFAULT_TIMER_MODE,
  sessionId,
  hideRing = false,
  onProgressTick,
  onTargetChange,
  onModeChange,
  onComplete,
}) => {
  const timer = useTimer({
    initialTarget: defaultTarget,
    initialMode: defaultMode,
    sessionId,
    onProgressTick,
    onTargetChange,
    onModeChange,
    onComplete,
  });
  const {
    target,
    setTarget,
    mode,
    toggleMode,
    elapsedMs,
    status,
    isSettingMode,
    progress,
    hasTarget,
    hasStarted,
    toggleSettingMode,
    togglePlayPause,
    reset,
  } = timer;

  const displayFormat = useMemo(() => getDisplayFormat(target), [target]);
  const mainDisplayMs = useMemo(
    () => getTimerMainDisplayMs(mode, durationToMs(target), elapsedMs, hasStarted),
    [mode, target, elapsedMs, hasStarted],
  );
  const mainDisplay = formatTimerTime(mainDisplayMs, displayFormat);
  const control = getTimerControlView(target, status, elapsedMs);
  const ringValue = hasStarted ? progress : 0;

  const PlayPauseIcon = control.playPauseIcon === 'pause' ? IconPlayerPause : IconPlayerPlay;

  const iconClass = (disabled: boolean) =>
    cn('size-5', disabled ? 'text-muted-foreground/35' : 'text-foreground');

  return (
    <div
      className={cn('relative inline-flex items-center justify-center', className)}
      style={{ width: size, height: size, ...style }}
      role="timer"
      aria-live="polite"
    >
      {hasTarget && !hideRing ? (
        <TimerProgressRing
          value={ringValue}
          className="pointer-events-none absolute inset-0"
        />
      ) : !hasTarget ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-full border border-default-200"
        />
      ) : null}

      <div
        className="relative z-10 flex flex-col items-center justify-center gap-3 rounded-full bg-background"
        style={{
          width: `${TIMER_INNER_DIAMETER_PERCENT}%`,
          height: `${TIMER_INNER_DIAMETER_PERCENT}%`,
        }}
      >
        <div className="flex items-center gap-1.5">
          <TimerTimeSetting
            value={target}
            onChange={setTarget}
            editable={isSettingMode && !control.isRunning}
          />
          <TimerModeToggle
            mode={mode}
            editable={isSettingMode && !control.isRunning}
            onToggle={toggleMode}
          />
        </div>

        <div className="text-4xl font-semibold tabular-nums tracking-tight text-foreground">
          {mainDisplay}
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            isIconOnly
            aria-label={control.playPauseLabel}
            isDisabled={control.playPauseDisabled}
            onPress={togglePlayPause}
          >
            <PlayPauseIcon className={iconClass(control.playPauseDisabled)} stroke={ICON_STROKE} />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            isIconOnly
            aria-label="重置"
            isDisabled={control.resetDisabled}
            onPress={reset}
          >
            <IconRefresh className={iconClass(control.resetDisabled)} stroke={ICON_STROKE} />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            isIconOnly
            aria-label={isSettingMode ? '完成设置' : '设置'}
            onPress={toggleSettingMode}
          >
            <IconSettings
              className={cn('size-5', isSettingMode ? 'text-primary' : 'text-foreground')}
              stroke={ICON_STROKE}
            />
          </Button>
        </div>
      </div>
    </div>
  );
};
